import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener datos de encuesta de estilo de vida de Contentful
 * Incluye contenido para cuestionarios de estilo de vida
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulLifeStyleSurvey = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'life-style-survey'] as const,
		queryFn: queriesApi.getLifeStyleSurvey,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Life Style Survey desde Contentful:', error)
	}

	// Procesar datos de encuesta de estilo de vida
	const contenidosRetosCollection = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return rawData?.contenidosRetosCollection?.items || []
		} catch (transformError) {
			console.error('Error al procesar datos de encuesta de estilo de vida:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Mapear datos para el template (compatibilidad con estructura esperada)
	const contentLifeStyleSurvey = useMemo(() => {
		if (!rawData || isLoading || error) return null

		try {
			const items = rawData?.contenidosRetosCollection?.items
			if (!items || items.length === 0) return null

			// Tomar el primer item y mapear a la estructura esperada
			const firstItem = items[0]
			return {
				tituloCard: firstItem?.tituloCard,
				descripcionDelReto: firstItem?.descripcionDelReto,
				textoBoton: firstItem?.textoBoton,
				imagenReto: firstItem?.imagenReto
			}
		} catch (transformError) {
			console.error('Error al mapear datos de encuesta de estilo de vida:', transformError)
			return null
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		contenidosRetosCollection,
		contentLifeStyleSurvey,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulLifeStyleSurvey
